import Papa from "papaparse";
import { useToast } from "../../../shared/hooks";
import { cleanText, parseCurrency, validateHeaders, normalizeHeader, fieldValidators } from "../utils/excelUtils";

export interface ExcelImportOptions {
	requiredHeaders: Record<string, string[]>;
	onSuccess: (data: any[]) => void;
	onError?: (errors: string[]) => void;
	onStart?: () => void;
	onFinish?: () => void;
	limit?: number;
}

export const useExcelImport = ({ requiredHeaders, onSuccess, onError, onStart, onFinish, limit = 100 }: ExcelImportOptions) => {
	const toast = useToast();

	const handleFile = (file: File) => {
		onStart?.();

		const reader = new FileReader();

		reader.onload = () => {
			const csvText = reader.result as string;

			Papa.parse(csvText, {
				header: true,
				skipEmptyLines: true,
				complete: (results) => {
					const rawRows = results.data as Record<string, string>[];

					if (!rawRows.length) {
						toast.present({ type: "error", messageOrContent: "Arquivo CSV vazio." });
						onError?.(["Nenhum dado encontrado"]);
						onFinish?.();
						return;
					}

					if (rawRows.length > limit) {
						toast.present({
							type: "error",
							messageOrContent: `O arquivo excede o limite de ${limit} linhas permitidas. Envie no máximo ${limit} registros por vez.`,
						});
						onError?.([`Máximo de ${limit} linhas permitidas. Seu arquivo tem ${rawRows.length}.`]);
						onFinish?.();
						return;
					}

					// 🔍 Obtem os headers da primeira linha
					const headerRow = Object.keys(rawRows[0]);
					const normalizedHeaders = headerRow.map(normalizeHeader);

					// ✅ Valida headers
					const validation = validateHeaders(normalizedHeaders, requiredHeaders);
					if (!validation.valid) {
						toast.present({ type: "error", messageOrContent: "Campos obrigatórios ausentes" });
						onError?.(validation.missing);
						onFinish?.();
						return;
					}

					// 🔁 Mapeia aliases -> nome padrão
					const aliasToStandard: Record<string, string> = {};
					for (const [standard, aliases] of Object.entries(requiredHeaders)) {
						for (const alias of aliases) {
							aliasToStandard[normalizeHeader(alias)] = standard;
						}
					}
					for (const [key, { aliases }] of Object.entries(fieldValidators)) {
						for (const alias of aliases) {
							aliasToStandard[normalizeHeader(alias)] = key;
						}
					}

					// 🔁 Processa os dados
					const processedData = rawRows.slice(0, limit).map((row) => {
						const normalizedRow: Record<string, any> = {};

						for (const colName in row) {
							const rawValue = row[colName] ?? "";
							const cleanedKey = normalizeHeader(colName);
							const standardKey = aliasToStandard[cleanedKey] || cleanedKey;
							let finalValue: any = cleanText(String(rawValue));

							const fieldValidation = fieldValidators[standardKey];
							if (fieldValidation?.validators.length) {
								for (const validate of fieldValidation.validators) {
									const result = validate(finalValue);
									finalValue = result.value;
								}
							} else if (standardKey.includes("valor") || ["aluguel", "condominio", "taxa"].includes(standardKey)) {
								finalValue = parseCurrency(String(rawValue));
							}

							normalizedRow[standardKey] = finalValue;
						}

						normalizedRow.status = "importado";
						normalizedRow.mensagem = "";

						return normalizedRow;
					});

					onSuccess(processedData);
					onFinish?.();
				},
				error: (err: any) => {
					console.error("Erro no PapaParse:", err);
					toast.present({ type: "error", messageOrContent: "Erro ao processar o CSV" });
					onError?.(["Erro ao processar o CSV"]);
					onFinish?.();
				},
			});
		};

		reader.onerror = () => {
			toast.present({ type: "error", messageOrContent: "Erro ao ler o arquivo CSV." });
			onError?.(["Erro ao ler o arquivo CSV."]);
			onFinish?.();
		};

		reader.readAsText(file, "UTF-8");
	};

	return { handleFile };
};
